import { useState } from 'react';
import { notify } from '@/lib/notify';
import { Save, Loader2, Zap, Play, Users } from 'lucide-react';
import type { SegmentDefinition } from '@masters-club/validation';
import { translateErrorMessage } from '@/providers/notification-provider';
import { FormHeader } from '@/components/forms';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { EventStep } from './steps/EventStep';
import { ActionsStep } from './steps/ActionsStep';
import { AudienceStep } from './steps/AudienceStep';
import { createDefaultAction } from '../config/actions';
import {
  isBroadcastEvent,
  type TriggerEventType,
  type TriggerAction,
  type TriggerFormData,
  type Trigger,
} from '../types';

interface TriggerWizardProps {
  trigger?: Trigger;
  isLoading: boolean;
  onSubmit: (data: TriggerFormData) => Promise<void>;
  onBack: () => void;
  showHeader?: boolean;
}

export function TriggerWizard({ trigger, isLoading, onSubmit, onBack, showHeader = true }: TriggerWizardProps) {
  const isEdit = !!trigger;

  const [name, setName] = useState(trigger?.name ?? '');
  const [eventType, setEventType] = useState<TriggerEventType | null>(
    trigger?.eventType ?? null
  );
  const [delay, setDelay] = useState(trigger?.delay ?? 0);
  const [isActive, setIsActive] = useState(trigger?.isActive ?? true);
  const [actions, setActions] = useState<TriggerAction[]>(
    trigger?.actions?.length ? trigger.actions : [createDefaultAction('send_notification')]
  );
  const [audienceDefinition, setAudienceDefinition] = useState<SegmentDefinition | null>(
    trigger?.audienceDefinition ?? null,
  );
  const [audienceSegmentId, setAudienceSegmentId] = useState<number | null>(
    trigger?.audienceSegmentId ?? null,
  );

  const handleSubmit = async () => {
    if (!eventType || !name.trim()) {
      notify.error('Заполните название и выберите событие');
      return;
    }

    if (actions.length === 0) {
      notify.error('Добавьте хотя бы одно действие');
      return;
    }

    const hasAudience =
      audienceSegmentId !== null ||
      (audienceDefinition !== null && audienceDefinition.groups.length > 0);
    if (isBroadcastEvent(eventType) && !hasAudience) {
      notify.error('Для broadcast-события необходимо указать аудиторию');
      return;
    }

    try {
      const formData: TriggerFormData = {
        name: name.trim(),
        eventType,
        actions,
        delay,
        isActive,
        audienceDefinition,
        audienceSegmentId,
      };

      await onSubmit(formData);
    } catch (error) {
      const message = error instanceof Error ? translateErrorMessage(error.message) : 'Ошибка при сохранении';
      notify.error(message);
    }
  };

  return (
    <div className="space-y-6">
      {showHeader && (
        <FormHeader
          title={isEdit ? 'Редактировать триггер' : 'Создать триггер'}
          onBack={onBack}
          sticky
        />
      )}

      <Tabs defaultValue="event">
        <TabsList>
          <TabsTrigger value="event" className="gap-2">
            <Zap className="h-4 w-4" />
            Событие
          </TabsTrigger>
          <TabsTrigger value="audience" className="gap-2">
            <Users className="h-4 w-4" />
            Аудитория
          </TabsTrigger>
          <TabsTrigger value="actions" className="gap-2">
            <Play className="h-4 w-4" />
            Действия
          </TabsTrigger>
        </TabsList>

        <TabsContent value="event" forceMount className="mt-6 data-[state=inactive]:hidden">
          <EventStep
            name={name}
            eventType={eventType}
            delay={delay}
            onNameChange={setName}
            onEventChange={setEventType}
            onDelayChange={setDelay}
          />
        </TabsContent>

        <TabsContent value="audience" forceMount className="mt-6 data-[state=inactive]:hidden">
          <AudienceStep
            eventType={eventType}
            audienceDefinition={audienceDefinition}
            audienceSegmentId={audienceSegmentId}
            onAudienceDefinitionChange={setAudienceDefinition}
            onAudienceSegmentIdChange={setAudienceSegmentId}
            disabled={isLoading}
          />
        </TabsContent>

        <TabsContent value="actions" forceMount className="mt-6 data-[state=inactive]:hidden">
          <ActionsStep
            actions={actions}
            isActive={isActive}
            onChange={setActions}
            onActiveChange={setIsActive}
          />
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={isLoading || actions.length === 0}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {isEdit ? 'Сохранить' : 'Создать триггер'}
        </Button>
      </div>
    </div>
  );
}
